import { supabase } from '@/integrations/supabase/client';
import { validatePolicyFile } from '@/lib/validatePolicyFile';

interface CreatePolicyData {
  name: string;
  category: string;
  description?: string;
  policyOwner: string;
  reviewCadenceMonths: number;
  effectiveDate: string;
  nextReviewDate?: string;
  roleGroups: string[];
  individualStaffIds?: string[];
  file: File;
  versionNotes?: string;
  status: 'draft' | 'pending_approval';
}

export async function createPolicy(data: CreatePolicyData): Promise<string> {
  // Step 0: Validate file before any upload attempt
  const validation = validatePolicyFile(data.file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  // Step 1: Resolve clinic_id server-side, generate the policy id up front,
  // and upload the file DIRECTLY to its final path. No temp path, no move —
  // the client-generated id lets us build the final path before the insert,
  // eliminating the storage.move() call (RLS denies move: no UPDATE policy
  // on storage.objects). Mirrors the working useUploadPolicyVersion flow.
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData?.user) {
    throw new Error('Not authenticated');
  }

  const { data: clinicId, error: clinicError } = await supabase.rpc(
    'get_user_clinic_id',
    { _user_id: userData.user.id }
  );
  if (clinicError || !clinicId) {
    throw new Error('Clinic not found');
  }

  const policyId = crypto.randomUUID();
  const finalPath = `${clinicId}/${policyId}/v1_${data.file.name}`;

  // Upload BEFORE the policy insert. If the insert later fails we remove this
  // file (orphan-file cleanup, GC-able) and NO policy row is created —
  // preserving the no-orphan-policy invariant and the no-DELETE design on the
  // policies table.
  const { error: uploadError } = await supabase.storage
    .from('policy-documents')
    .upload(finalPath, data.file);

  if (uploadError) {
    console.error('Upload error:', uploadError);
    throw new Error('Failed to upload document');
  }

  // Step 2: Calculate next review date (use override if provided)
  let nextReviewDateStr: string;
  if (data.nextReviewDate) {
    nextReviewDateStr = data.nextReviewDate;
  } else {
    const effective = new Date(data.effectiveDate);
    const nextReview = new Date(effective);
    nextReview.setMonth(nextReview.getMonth() + data.reviewCadenceMonths);
    nextReviewDateStr = nextReview.toISOString().split('T')[0];
  }
  // Step 3: Insert policy via SECURITY DEFINER RPC. clinic_id and created_by
  // are derived server-side from the caller's profile — never from the client
  // (OWASP A04). Returns the new policy id plus the server-confirmed clinic_id
  // and profile_id used for the storage path and assignment records below.
  const { data: policyRows, error: policyError } = await supabase.rpc(
    'create_policy_record',
    {
      p_policy_id: policyId,
      p_name: data.name,
      p_category: data.category,
      p_description: data.description || null,
      p_status: data.status,
      p_effective_date: data.effectiveDate,
      p_review_cadence_months: data.reviewCadenceMonths,
      p_next_review_date: nextReviewDateStr,
      p_policy_owner: data.policyOwner,
    }
  );

  const policy = policyRows?.[0];

  if (policyError || !policy) {
    console.error('Policy insert error:', policyError);
    // Clean up uploaded file
    await supabase.storage.from('policy-documents').remove([finalPath]);
    throw new Error('Failed to create policy');
  }

  const policyClinicId = policy.clinic_id;
  const policyProfileId = policy.profile_id;

  // Step 5: Insert version via SECURITY DEFINER RPC (version_status server-set).
  // Direct INSERT is no longer permitted — "Admins can insert versions" RLS
  // was dropped in S30. Must THROW on failure: a silent failure here leaves
  // the policy + storage object with no matching version row.
  const { error: versionError } = await supabase.rpc('create_policy_version_record', {
    p_policy_id: policy.policy_id,
    p_file_url: finalPath,
    p_notes: data.versionNotes || null,
  });

  if (versionError) {
    console.error('Version insert error:', versionError);
    throw new Error('Failed to create policy version');
  }

  // Step 6: Resolve role groups and insert assignments
  try {
    let userIds: string[] = [];

    // Get all profiles for this clinic
    const { data: clinicProfiles } = await supabase
      .from('profiles')
      .select('id, user_id')
      .eq('clinic_id', policyClinicId);

    if (clinicProfiles && clinicProfiles.length > 0) {
      for (const group of data.roleGroups) {
        if (group === 'all_staff') {
          // All profiles in clinic
          userIds.push(...clinicProfiles.map(p => p.id));
        } else {
          // Get user_ids that have the required role
          let roleFilter: string[] = [];
          
          if (group === 'clinical') {
            roleFilter = ['staff'];
          } else if (group === 'administrative') {
            roleFilter = ['admin'];
          } else if (group === 'management') {
            roleFilter = ['admin'];
          }

          if (roleFilter.length > 0) {
            const { data: roleUsers } = await supabase
              .from('user_roles')
              .select('user_id')
              .in('role', roleFilter as ('admin' | 'staff' | 'read_only')[]);

            if (roleUsers) {
              // Find profiles that match these user_ids
              const matchingUserIds = new Set(roleUsers.map(r => r.user_id));
              const matchingProfiles = clinicProfiles.filter(p => 
                matchingUserIds.has(p.user_id)
              );
              userIds.push(...matchingProfiles.map(p => p.id));
            }
          }
        }
      }
    }

    // Add individually selected staff (profiles.id directly)
    if (data.individualStaffIds && data.individualStaffIds.length > 0) {
      userIds.push(...data.individualStaffIds);
    }

    // Deduplicate
    userIds = [...new Set(userIds)];

    // Insert assignments
    if (userIds.length > 0) {
      const assignments = userIds.map(userId => ({
        policy_id: policy.policy_id,
        user_id: userId,
        assignment_type: (data.individualStaffIds?.includes(userId) ? 'individual' : 'role') as string,
        assigned_role: data.individualStaffIds?.includes(userId) ? null : (data.roleGroups[0] || null),
        assigned_by: policyProfileId,
      }));

      const { error: assignmentError } = await supabase
        .from('policy_assignments')
        .insert(assignments);

      if (assignmentError) {
        console.error('Assignment insert error:', assignmentError);
      }
    }
  } catch (err) {
    console.error('Assignment exception:', err);
  }

  return policy.policy_id;
}
